import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MessageCircle, Compass, Plus, Pencil, Check, X, Search } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Footer } from "@/components/Footer";

const softDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const lastLine = (c) => {
  const msgs = c.messages || [];
  if (msgs.length === 0) return "Not yet begun.";
  const last = msgs[msgs.length - 1];
  const who = last.role === "user" ? "You" : "Mirror";
  const text = (last.content || "").replace(/\s+/g, " ").trim();
  return `${who}: ${text.slice(0, 110)}${text.length > 110 ? "…" : ""}`;
};

export default function History() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    api
      .get("/conversations")
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]));
  }, []);

  const startRename = (c) => {
    setRenamingId(c.id);
    setDraft(c.title);
  };

  const commitRename = async (c) => {
    const title = draft.trim();
    setRenamingId(null);
    if (!title || title === c.title) return;
    try {
      await api.patch(`/conversations/${c.id}`, { title });
      setItems((prev) => prev.map((x) => (x.id === c.id ? { ...x, title } : x)));
    } catch (err) {
      toast(err.response?.data?.detail || "Could not rename.");
    }
  };

  const q = query.trim().toLowerCase();
  const matches = (c) => {
    if (!q) return true;
    if ((c.title || "").toLowerCase().includes(q)) return true;
    return (c.messages || []).some((m) => (m.content || "").toLowerCase().includes(q));
  };
  const filtered = items ? items.filter(matches) : items;

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 px-6 sm:px-10 py-8">
      <header className="max-w-3xl mx-auto flex items-center justify-between">
        <button
          data-testid="history-back-button"
          onClick={() => navigate("/begin")}
          className="flex items-center gap-2 text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500 text-sm"
        >
          <ArrowLeft size={16} /> Begin
        </button>
        <span className="text-xs tracking-[0.35em] uppercase text-[var(--mirror-text-2)]">
          Conversations
        </span>
      </header>

      <main className="max-w-3xl mx-auto mt-20">
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight"
        >
          Every conversation you've had.
        </motion.h1>
        <p className="mt-4 text-[var(--mirror-text-3)] text-sm max-w-md">
          Nothing is lost. Return to any conversation and continue where you left off,
          or begin a new one whenever you like.
        </p>

        <button
          data-testid="history-new-button"
          onClick={() => navigate("/begin")}
          className="mt-8 inline-flex items-center gap-2 rounded-full border border-[rgba(242,236,228,0.15)] px-6 py-2 text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-2)] hover:text-[var(--mirror-text)] hover:border-[rgba(242,236,228,0.3)] transition-colors duration-500"
        >
          <Plus size={14} /> New conversation
        </button>

        <div className="mt-8 relative">
          <Search
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--mirror-text-3)]"
          />
          <input
            data-testid="history-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your conversations…"
            className="w-full rounded-full bg-transparent border border-[var(--mirror-border)] focus:border-[var(--mirror-border-strong)] pl-11 pr-4 py-2.5 text-sm outline-none text-[var(--mirror-text)] transition-colors duration-500"
          />
        </div>

        {items === null && (
          <p className="mt-16 text-[var(--mirror-text-3)] text-sm">Gathering…</p>
        )}

        {items && items.length === 0 && (
          <div data-testid="history-empty-state" className="mt-20 text-center">
            <p className="font-serif-display text-2xl font-light text-[var(--mirror-text-2)]">
              No conversations yet.
            </p>
          </div>
        )}

        {items && items.length > 0 && filtered && filtered.length === 0 && (
          <div data-testid="history-search-empty" className="mt-20 text-center">
            <p className="font-serif-display text-2xl font-light text-[var(--mirror-text-2)]">
              Nothing matches "{query}".
            </p>
          </div>
        )}

        <div className="mt-12 flex flex-col gap-4">
          {filtered?.map((c, i) => (
            <motion.div
              key={c.id}
              data-testid={`history-item-${c.id}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 * i }}
              className="rounded-2xl border border-[rgba(242,236,228,0.08)] hover:border-[rgba(242,236,228,0.22)] p-6 transition-colors duration-500"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  {c.mode === "guided" ? (
                    <Compass size={16} className="text-[var(--mirror-accent)] shrink-0" />
                  ) : (
                    <MessageCircle size={16} className="text-[var(--mirror-accent)] shrink-0" />
                  )}
                  {renamingId === c.id ? (
                    <div className="flex items-center gap-2 min-w-0">
                      <input
                        data-testid={`history-rename-input-${c.id}`}
                        autoFocus
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitRename(c);
                          if (e.key === "Escape") setRenamingId(null);
                        }}
                        className="bg-transparent border-b border-[rgba(242,236,228,0.3)] outline-none text-[var(--mirror-text)] font-serif-display text-xl min-w-0"
                      />
                      <button
                        data-testid={`history-rename-save-${c.id}`}
                        onClick={() => commitRename(c)}
                        className="text-[var(--mirror-accent)] shrink-0"
                        aria-label="Save name"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={() => setRenamingId(null)}
                        className="text-[var(--mirror-text-3)] shrink-0"
                        aria-label="Cancel"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        data-testid={`history-open-${c.id}`}
                        onClick={() => navigate(`/chat/${c.id}`)}
                        className="font-serif-display text-xl font-light text-[var(--mirror-text)] truncate hover:opacity-70 transition-opacity duration-500 text-left"
                      >
                        {c.title}
                      </button>
                      <button
                        data-testid={`history-rename-${c.id}`}
                        onClick={() => startRename(c)}
                        className="text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500 shrink-0"
                        aria-label="Rename conversation"
                      >
                        <Pencil size={13} />
                      </button>
                      {c.ended && (
                        <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--mirror-text-3)] border border-[rgba(242,236,228,0.12)] rounded-full px-2 py-0.5 shrink-0">
                          Reflected
                        </span>
                      )}
                    </>
                  )}
                </div>
                <span className="text-xs text-[var(--mirror-text-3)] whitespace-nowrap shrink-0">
                  {softDate(c.updated_at)}
                </span>
              </div>
              <button
                data-testid={`history-snippet-${c.id}`}
                onClick={() => navigate(`/chat/${c.id}`)}
                className="mt-3 text-sm text-[var(--mirror-text-3)] leading-relaxed text-left w-full hover:text-[var(--mirror-text-2)] transition-opacity duration-500"
              >
                {lastLine(c)}
              </button>
            </motion.div>
          ))}
        </div>
      </main>
      </div>
      <Footer />
    </div>
  );
}
